// OrderReassign.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { User, RefreshCw } from "lucide-react";
import { ordersApi, employeesApi } from "../services/api"; // path adjust karo
import { useTheme } from "../context/ThemeContext";
import ThemeSelect from "../components/ThemeSelect";
import Pagination from "../components/Pagination";
import ExpandableSearchBar from "../components/ExpandableSearchBar";

// ─── Searchable Employee Dropdown ──────────────────────────────────────────
function SearchableEmployeeSelect({
  value,
  valueName,
  onChange,
  employees,
  isDark,
  currentTheme,
  placeholder = "All Employees",
  includeAll = true,
  excludeId,
  className = "w-48 desktop:w-64",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = employees
    .filter((e) => !excludeId || String(e.id) !== String(excludeId))
    .filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        String(e.employeeId || e.empCode || e.id).toLowerCase().includes(q)
    );

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          setQuery("");
        }}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm transition-all ${
          isDark
            ? "bg-[#111827] border-white/10 text-gray-200"
            : "bg-white border-gray-300 text-gray-700"
        }`}
        style={{ borderColor: open ? currentTheme.color : undefined }}
      >
        <span className="flex items-center gap-2 truncate">
          <User className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{valueName || placeholder}</span>
        </span>
        <span
          className={`text-gray-400 text-xs transition-transform shrink-0 ml-2 ${
            open ? "rotate-180" : ""
          }`}
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          className={`absolute left-0 top-full mt-2 w-full rounded-xl shadow-xl border z-50 overflow-hidden ${
            isDark ? "bg-[#1f2937] border-white/10" : "bg-white border-gray-200"
          }`}
        >
          {/* Search box */}
          <div className={`p-2 border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search employee..."
              className={`w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 ${
                isDark
                  ? "bg-[#111827] border-white/10 text-white placeholder-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-900"
              }`}
              style={{ "--tw-ring-color": currentTheme.color }}
            />
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto py-1">
            {includeAll && (
              <button
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                  isDark ? "hover:bg-white/5" : "hover:bg-gray-50"
                }`}
                style={{
                  backgroundColor: !value ? `${currentTheme.color}20` : undefined,
                  color: !value ? currentTheme.color : isDark ? "#e5e7eb" : "#374151",
                  fontWeight: !value ? 600 : 400,
                }}
              >
                All Employees
              </button>
            )}
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400 text-center">
                Koi employee nahi mila
              </p>
            ) : (
              filtered.map((e) => {
                const isSel = String(value) === String(e.id);
                return (
                  <button
                    key={e.id}
                    onClick={() => {
                      onChange(e);
                      setOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left transition-colors ${
                      isDark ? "hover:bg-white/5" : "hover:bg-gray-50"
                    }`}
                    style={{
                      backgroundColor: isSel ? `${currentTheme.color}20` : undefined,
                    }}
                  >
                    <span
                      className={`block text-sm font-medium ${
                        isSel ? "" : isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                      style={{ color: isSel ? currentTheme.color : undefined }}
                    >
                      {e.name}
                    </span>
                    <span className="block text-xs text-gray-400">
                      Emp: {e.employeeId || e.empCode || e.id}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function OrderReassign() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === "#FFFFFF";

  // ─── State ───────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [employees, setEmployees] = useState([]);
  const [fromEmp, setFromEmp] = useState("");
  const [fromEmpName, setFromEmpName] = useState("");
  const [toEmp, setToEmp] = useState("");
  const [reason, setReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [orderLimit, setOrderLimit] = useState("20 Orders");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [result, setResult] = useState(null); // ✅ { success, failed, total, errors }
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const currentPageRef = useRef(1);

  // ─── Helpers ─────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const statusColor = (s) => {
    const map = {
      PENDING: "bg-amber-100 text-amber-700",
      PROCESSING: "bg-sky-100 text-sky-700",
      CONFIRMED: "bg-emerald-100 text-emerald-700",
      VERIFIED: "bg-teal-100 text-teal-700",
      DELIVERED: "bg-green-100 text-green-700",
      CANCELLED: "bg-red-100 text-red-700",
      DRAFT: "bg-gray-100 text-gray-600",
      OFD: "bg-blue-100 text-blue-700",
      IN_TRANSIT: "bg-indigo-100 text-indigo-700",
    };
    return map[s] || "bg-gray-100 text-gray-600";
  };

  // ─── API Calls ───────────────────────────────────────────────────────
  useEffect(() => {
    employeesApi
      .getAllEmployees()
      .then((d) => setEmployees(Array.isArray(d) ? d : d?.content || []))
      .catch(() => {});
  }, []);

  const fetchOrders = useCallback(
    async ({ overridePage, overrideSize } = {}) => {
      setLoading(true);
      try {
        const page = overridePage !== undefined ? overridePage : currentPageRef.current;
        const params = {
          page: page - 1, // backend 0-indexed
          size: overrideSize !== undefined ? parseInt(overrideSize) : parseInt(orderLimit),
        };
        if (fromEmp) params.orderBy = fromEmp;
        if (searchTerm.trim()) params.searchTerm = searchTerm.trim();

        const r = await ordersApi.getAllOrders(params);

        const list = Array.isArray(r?.orders) ? r.orders
          : Array.isArray(r?.content) ? r.content
          : Array.isArray(r) ? r
          : [];

        setOrders(list);
        setTotalOrders(r?.totalCount ?? r?.totalElements ?? list.length);
      } catch (e) {
        showToast(e.message || "Orders load failed", "error");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    [fromEmp, searchTerm, orderLimit]
  );

  // Mount par ye dono effects chalte the → fetchOrders 2 baar. [fromEmp] wala hi
  // initial load hai (koi alag mount fetch nahi hai), isliye sirf search wale ka
  // pehla render skip karo.
  const skipFirstSearch = useRef(true);

  // Employee filter change → page 1 reset (+ initial load)
  useEffect(() => {
    setCurrentPage(1);
    currentPageRef.current = 1;
    fetchOrders({ overridePage: 1 });
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromEmp]);

  // Debounced search → page 1 reset
  useEffect(() => {
    if (skipFirstSearch.current) {
      skipFirstSearch.current = false;
      return;
    }
    const t = setTimeout(() => {
      setCurrentPage(1);
      currentPageRef.current = 1;
      fetchOrders({ overridePage: 1 });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ─── Selection ───────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) =>
      prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))
    );

  // ─── Reassign ────────────────────────────────────────────────────────
  const handleReassign = async () => {
    if (!toEmp || selected.size === 0) return;
    setSubmitting(true);
    try {
      const r = await ordersApi.bulkReassignOrders({
        orderIds: [...selected].map(Number),
        newAssignedToId: Number(toEmp),
        ...(reason.trim() && { reason: reason.trim() }),
      });

      // ✅ Result popup dikhao
      setResult({
        success: r?.success ?? 0,
        failed: r?.failed ?? 0,
        total: r?.total ?? selected.size,
        errors: r?.errors || [],
      });

      if ((r?.success ?? 0) > 0) {
        setSelected(new Set());
        setShowModal(false);
        setToEmp("");
        setReason("");
        fetchOrders(); // ref se current page pick hoga
      }
    } catch (e) {
      showToast(e.message || "Reassign failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-[#0f172a]" : "bg-gray-50"}`}>
      <div className="flex flex-col gap-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? "text-white" : "text-gray-800"}`}>
              Order Reassignment{" "}
              <span className="text-gray-500 text-xl desktop:text-2xl">
                ({totalOrders.toLocaleString("en-IN")})
              </span>
            </h1>
            <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Orders ko ek employee se dusre ko transfer karo
            </p>
          </div>
          <button
            onClick={() => fetchOrders()}
            className={`flex items-center gap-2 text-sm border px-4 py-2 rounded-lg transition-all ${
              isDark
                ? "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className={`rounded-xl border p-4 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Orders</p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? "text-white" : "text-gray-800"}`}>
              {totalOrders.toLocaleString("en-IN")}
            </p>
          </div>
          <div className={`rounded-xl border p-4 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Selected</p>
            <p className="text-2xl font-bold mt-1" style={{ color: currentTheme.color }}>
              {selected.size}
            </p>
          </div>
          <div className={`rounded-xl border p-4 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Filtered By</p>
            <p className={`text-lg font-semibold mt-1 truncate ${isDark ? "text-white" : "text-gray-800"}`}>
              {fromEmpName || "All Employees"}
            </p>
          </div>
        </div>

        {/* ── Filters Row ── */}
        <div className="flex items-center space-x-4 flex-wrap gap-y-3">
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by order id or customer name"
          />

          <SearchableEmployeeSelect
            value={fromEmp}
            valueName={fromEmpName}
            onChange={(emp) => {
              setFromEmp(emp?.id || "");
              setFromEmpName(emp?.name || "");
            }}
            employees={employees}
            isDark={isDark}
            currentTheme={currentTheme}
          />

          <ThemeSelect
            value={orderLimit}
            onChange={(val) => {
              setOrderLimit(val);
              setCurrentPage(1);
              currentPageRef.current = 1;
              fetchOrders({ overridePage: 1, overrideSize: val });
            }}
            options={["20 Orders", "50 Orders", "100 Orders"]}
            className="w-44 desktop:w-60"
          />
        </div>

        {/* ── Table ── */}
        <div className={`rounded-xl border overflow-hidden ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="text-white" style={{ background: currentTheme.color }}>
                <tr>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left w-12">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer"
                      checked={orders.length > 0 && selected.size === orders.length}
                      onChange={toggleAll}
                    />
                  </th>
                  {["ORDER ID", "CUSTOMER", "PHONE", "STATUS", "ASSIGNED TO"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-sm font-semibold uppercase whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <div
                          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: `${currentTheme.color} transparent transparent transparent` }}
                        />
                        <span className="text-gray-500">Loading orders...</span>
                      </div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      Koi orders nahi mile
                    </td>
                  </tr>
                ) : (
                  orders.map((order, index) => (
                    <tr
                      key={order.id}
                      onClick={() => toggleSelect(order.id)}
                      className={`cursor-pointer transition-colors ${
                        selected.has(order.id)
                          ? isDark ? "bg-teal-500/10" : "bg-teal-50"
                          : isDark
                          ? index % 2 === 0 ? "bg-[#111827]" : "bg-white/[0.02]"
                          : index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      } ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer"
                          checked={selected.has(order.id)}
                          onChange={() => toggleSelect(order.id)}
                        />
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {order.orderNo || order.orderId || order.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {order.customerName || order.name || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base text-gray-500">
                          {order.phone || order.contactNumber || order.mobileNumber || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`px-3 py-1 text-xs desktop:text-sm rounded-full font-medium ${statusColor(order.status)}`}>
                          {order.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {order.assignedToName || order.orderByName || order.createdByName || order.employeeName || "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalItems={totalOrders}
              itemsPerPage={parseInt(orderLimit)}
              onPageChange={(page) => {
                setCurrentPage(page);
                currentPageRef.current = page;
                window.scrollTo({ top: 0, behavior: "smooth" });
                fetchOrders({ overridePage: page });
              }}
            />
          </div>
        </div>

        {/* ── Sticky Action Bar ── */}
        {selected.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-full shadow-xl px-6 py-3 flex items-center gap-4 z-40">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <button onClick={() => setSelected(new Set())} className="text-sm text-gray-300 hover:text-white">
              Clear
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="text-sm font-semibold px-5 py-2 rounded-full text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: currentTheme.color }}
            >
              Reassign →
            </button>
          </div>
        )}

        {/* ── Reassign Modal ── */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className={`rounded-2xl shadow-2xl w-full max-w-md ${isDark ? "bg-[#1f2937]" : "bg-white"}`}>
              <div className="text-white px-6 py-4 rounded-t-2xl" style={{ background: currentTheme.color }}>
                <h3 className="font-semibold text-lg">Reassign Orders</h3>
                <p className="text-white/80 text-sm mt-0.5">{selected.size} order(s) selected</p>
              </div>
              <div className="p-6">
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Assign to employee
                </label>

                <div className="mb-4">
                  <SearchableEmployeeSelect
                    value={toEmp}
                    valueName={employees.find((e) => String(e.id) === String(toEmp))?.name || ""}
                    onChange={(emp) => setToEmp(emp?.id || "")}
                    employees={employees}
                    isDark={isDark}
                    currentTheme={currentTheme}
                    placeholder="Select employee..."
                    includeAll={false}
                    excludeId={fromEmp}
                    className="w-full"
                  />
                </div>

                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Reason <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. Floor workload balancing"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 mb-6 resize-none ${
                    isDark
                      ? "bg-[#111827] border-white/10 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  style={{ "--tw-ring-color": currentTheme.color }}
                />

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className={`border px-5 py-2.5 rounded-lg text-sm transition-colors ${
                      isDark
                        ? "border-white/10 text-gray-300 hover:bg-white/5"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReassign}
                    disabled={!toEmp || submitting}
                    className="text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    {submitting ? "Reassigning..." : "Confirm Reassign"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ✅ Result Popup ── */}
        {result && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className={`rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center ${isDark ? "bg-[#1f2937]" : "bg-white"}`}>
              <div
                className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl ${
                  result.failed === 0
                    ? "bg-emerald-100 text-emerald-600"
                    : result.success > 0
                    ? "bg-amber-100 text-amber-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                {result.failed === 0 ? "✓" : result.success > 0 ? "⚠" : "✗"}
              </div>

              <h3 className={`text-xl font-bold mb-1 ${isDark ? "text-white" : "text-gray-800"}`}>
                {result.failed === 0
                  ? "Reassign Successful!"
                  : result.success > 0
                  ? "Partially Completed"
                  : "Reassign Failed"}
              </h3>
              <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {result.success} of {result.total} order(s) reassigned
                {result.failed > 0 && `, ${result.failed} failed`}
              </p>

              {result.errors?.length > 0 && (
                <div
                  className={`text-left text-xs rounded-lg p-3 mb-4 max-h-32 overflow-y-auto ${
                    isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
                  }`}
                >
                  {result.errors.map((err, i) => (
                    <p key={i}>• {err}</p>
                  ))}
                </div>
              )}

              <button
                onClick={() => setResult(null)}
                className="text-white px-8 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                style={{ backgroundColor: currentTheme.color }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* ── Toast ── */}
        {toast && (
          <div
            className={`fixed top-6 right-6 z-[9999] px-5 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
              toast.type === "error" ? "bg-red-600" : "bg-emerald-600"
            }`}
          >
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}